import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import GetDigipin from './components/GetDigipin';
import ConvertDigipin from './components/ConvertDigipin';
import CombinedDigipin from './components/CombinedDigipin';
import Pin2PointMain from './components/Pin2PointMain';
import BulkCSV from './components/BulkCSV';
import UsageHistory from './components/UsageHistory';
import Auth from './components/Auth';
import AdminDashboard from './components/AdminDashboard';
import AdminUsers from './components/AdminUsers';
import AdminUsage from './components/AdminUsage';
import Logo from './components/Logo'; // Import the new Logo component
import { getUserProfile } from './utils/auth';
import ProfilePage from './components/ProfilePage';
import UsageHistoryPage from './components/UsageHistoryPage';
import RouteIdentifierPage from './components/RouteIdentifierPage'; // Import the new page
import MyLocationsPage from './components/MyLocationsPage';
import LocationTest from './components/LocationTest'; // Import the test component
import DigipinFinder from './components/DigipinFinder'; // Import the new standalone component
import Pin2PointLeaflet from './components/Pin2PointLeaflet'; // Import the new Leaflet-based component
import { GoogleMapsProvider } from './contexts/GoogleMapsContext';
import { AddressProvider } from './contexts/AddressContext';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/" replace />;
};

// Admin Route Component
const AdminRoute = ({ children, isAuthenticated, isAdmin }) => {
  return isAuthenticated && isAdmin ? children : <Navigate to="/" replace />;
};

// Home Component
const Home = ({ isAuthenticated, isAdmin }) => (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '2.5rem', color: '#333' }}>Welcome to Pin2Point</h1>
        <p style={{ fontSize: '1.2rem', color: '#666', maxWidth: '600px', margin: '0 auto 1.5rem auto' }}>
            Your all-in-one solution for address management and route optimization.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
            {/* DigiPin Feature Box */}
            <div style={{
                padding: '1.5rem',
                border: '1px solid #eee',
                borderRadius: '8px',
                maxWidth: '300px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                background: '#fff'
            }}>
                <h2 style={{ marginTop: 0, color: '#007bff' }}>DigiPin Conversion</h2>
                <p>Instantly convert long, complex addresses into short, easy-to-share DigiPins, and vice-versa. Perfect for logistics, deliveries, and personal use.</p>
                {isAuthenticated ? (
                    <Link to="/home" style={{ textDecoration: 'none', color: '#007bff', fontWeight: 'bold' }}>Go to Pin2Point App &rarr;</Link>
                ) : (
                    <p style={{ marginTop: '2rem' }}>Please <Link to="/">Login</Link> to use this feature.</p>
                )}
            </div>

            {/* Route Optimizer Feature Box */}
            <div style={{
                padding: '1.5rem',
                border: '1px solid #eee',
                borderRadius: '8px',
                maxWidth: '300px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                background: '#fff'
            }}>
                <h2 style={{ marginTop: 0, color: '#007bff' }}>Optimum Route Optimizer</h2>
                <p>Find the shortest aerial and road distance from a new location to your list of saved addresses. Ideal for planning deliveries and multi-stop journeys.</p>
                {isAuthenticated ? (
                    <Link to="/route-identifier" style={{ textDecoration: 'none', color: '#007bff', fontWeight: 'bold' }}>Go to Route Optimizer &rarr;</Link>
                ) : (
                    <p style={{ marginTop: '2rem' }}>Please <Link to="/">Login</Link> to use this feature.</p>
                )}
            </div>
        </div>

        {!isAuthenticated && (
            <p style={{ marginTop: '3rem', fontSize: '1.1rem' }}>
                <Link to="/">Login or Register</Link> to get started.
            </p>
        )}
    </div>
);

const AppContent = () => {
  const location = useLocation();
  const isRouteIdentifierPage = location.pathname === '/route-identifier';

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false); // Add dropdown state
  const dropdownRef = useRef(null);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        const userProfile = await getUserProfile();
        if (userProfile) {
          setUser(userProfile);
          setIsAuthenticated(true);
          
          // Load addresses synchronously if user is authenticated
          const addressesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/addresses`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          
          if (addressesResponse.ok) {
            const addressesData = await addressesResponse.json();
            console.log('✅ Addresses loaded synchronously on app start:', addressesData.length, 'addresses');
          } else {
            console.warn('Failed to load addresses on app start');
          }
        } else {
          // Token is invalid, clear storage
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('token_expires');
        }
      } catch (error) {
        // Clear invalid tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('token_expires');
      }
    }
    setLoading(false);
  };

  const handleLogin = async (loginData) => {
    setIsAuthenticated(true);
    // Fetch user profile and addresses after login
    try {
      const userProfile = await getUserProfile();
      setUser(userProfile);
      
      // Load addresses synchronously after login
      const addressesResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/addresses`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      
      if (addressesResponse.ok) {
        const addressesData = await addressesResponse.json();
        console.log('✅ Addresses loaded synchronously after login:', addressesData.length, 'addresses');
      } else {
        console.warn('Failed to load addresses after login');
      }
    } catch (error) {
      console.error('Error loading user data after login:', error);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  const mainContentStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: isRouteIdentifierPage ? 0 : '2rem'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      {location.pathname !== '/pin2point' && location.pathname !== '/digipin-finder' && (
        <nav style={{ 
          padding: '1rem', 
          borderBottom: '1px solid #ccc',
          backgroundColor: '#f8f9fa',
          flexShrink: 0 // Prevent navbar from shrinking
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Left Side: Logo and Company Name */}
            <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Logo />
              <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#333' }}>Pin2Point</span>
            </Link>

            {/* Right Side: Navigation Links and Settings */}
            <div>
              {isAuthenticated && (
                <>
                  <Link to="/pin2point" style={{ marginRight: 16, textDecoration: 'none', color: '#007bff', fontWeight: location.pathname === '/pin2point' ? 'bold' : 'normal' }}>
                    Pin2Point
                  </Link>
                  <Link to="/pin2point-leaflet" style={{ marginRight: 16, textDecoration: 'none', color: '#28a745', fontWeight: location.pathname === '/pin2point-leaflet' ? 'bold' : 'normal' }}>
                    Pin2Point (Leaflet)
                  </Link>
                  <Link to="/digipin-finder" style={{ marginRight: 16, textDecoration: 'none', color: '#007bff', fontWeight: location.pathname === '/digipin-finder' ? 'bold' : 'normal' }}>
                    DigiPin Finder
                  </Link>
                  <Link to="/bulk-search" style={{ marginRight: 16, textDecoration: 'none', color: '#007bff', fontWeight: location.pathname === '/bulk-search' ? 'bold' : 'normal' }}>
                    Batch Processor
                  </Link>
                  <Link to="/my-locations" style={{ marginRight: 16, textDecoration: 'none', color: '#007bff', fontWeight: location.pathname === '/my-locations' ? 'bold' : 'normal' }}>
                    My Locations
                  </Link>
                  <Link to="/usage-history" style={{ marginRight: 16, textDecoration: 'none', color: '#007bff', fontWeight: location.pathname === '/usage-history' ? 'bold' : 'normal' }}>
                    History
                  </Link>
                  {user?.is_admin && (
                    <>
                      <Link to="/admin" style={{ marginRight: 16, textDecoration: 'none', color: '#dc3545', fontWeight: location.pathname === '/admin' ? 'bold' : 'normal' }}>
                        Admin
                      </Link>
                      <Link to="/admin/users" style={{ marginRight: 16, textDecoration: 'none', color: '#dc3545', fontWeight: location.pathname === '/admin/users' ? 'bold' : 'normal' }}>
                        Users
                      </Link>
                      <Link to="/admin/usage" style={{ marginRight: 16, textDecoration: 'none', color: '#dc3545', fontWeight: location.pathname === '/admin/usage' ? 'bold' : 'normal' }}>
                        Logs
                      </Link>
                    </>
                  )}
                  <div style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>
                    <button
                      style={{
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontSize: '16px',
                      }}
                      onClick={() => setShowDropdown((prev) => !prev)}
                    >
                      Settings &#x25BC;
                    </button>
                    {showDropdown && (
                      <div style={{
                        position: 'absolute',
                        right: 0,
                        background: 'white',
                        minWidth: '160px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        borderRadius: '4px',
                        zIndex: 2000,
                      }}>
                        <Link to="/profile" style={{ display: 'block', padding: '12px 16px', color: '#333', textDecoration: 'none' }} onClick={() => setShowDropdown(false)}>Profile</Link>
                        <button 
                            onClick={() => {
                                handleLogout();
                                setShowDropdown(false);
                            }}
                            style={{
                                display: 'block',
                                width: '100%',
                                padding: '12px 16px',
                                background: 'none',
                                border: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                color: '#dc3545',
                                fontSize: '14px'
                            }}
                        >
                            Logout
                        </button>
                      </div>
                    )}
                                     </div>
                 </>
               )}
               {!isAuthenticated && (
                 <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                   <Link to="/digipin-finder" style={{ textDecoration: 'none', color: '#28a745', fontWeight: '500' }}>
                     Digipin Finder
                   </Link>
                   <Link to="/" style={{ textDecoration: 'none', color: '#333' }}>
                     Login/Register
                   </Link>
                 </div>
               )}
             </div>
           </div>
         </nav>
       )}
      
      <div style={mainContentStyle}>
        <Routes>
          <Route path="/" element={
            isAuthenticated ? (
              <Navigate to="/pin2point" replace />
            ) : (
              <Auth 
                onLogin={handleLogin} 
                onLogout={handleLogout} 
                isAuthenticated={isAuthenticated}
                user={user}
              />
            )
          } />
          <Route path="/welcome" element={
            <Home isAuthenticated={isAuthenticated} isAdmin={user?.is_admin} />
          } />
          <Route path="/auth" element={
            <Auth 
              onLogin={handleLogin} 
              onLogout={handleLogout} 
              isAuthenticated={isAuthenticated}
              user={user}
            />
          } />
          <Route path="/profile" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <ProfilePage user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          } />
          <Route path="/home" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Navigate to="/pin2point" replace />
            </ProtectedRoute>
          } />
          <Route path="/pin2point" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <div style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '100vh',
                zIndex: 1000,
                backgroundColor: 'white',
                overflow: 'hidden'
              }}>
                <Pin2PointMain />
              </div>
            </ProtectedRoute>
          } />
          <Route path="/bulk-search" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <BulkCSV />
            </ProtectedRoute>
          } />
          <Route path="/usage-history" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <UsageHistoryPage />
            </ProtectedRoute>
          } />
          <Route path="/my-locations" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <MyLocationsPage />
            </ProtectedRoute>
          } />
          <Route path="/route-identifier" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <RouteIdentifierPage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <AdminRoute isAuthenticated={isAuthenticated} isAdmin={user?.is_admin}>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path="/admin/users" element={
            <AdminRoute isAuthenticated={isAuthenticated} isAdmin={user?.is_admin}>
              <AdminUsers />
            </AdminRoute>
          } />
          <Route path="/admin/usage" element={
            <AdminRoute isAuthenticated={isAuthenticated} isAdmin={user?.is_admin}>
              <AdminUsage />
            </AdminRoute>
          } />
          <Route path="/location-test" element={<LocationTest />} />
          <Route path="/digipin-finder" element={
            <div style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: '100vh',
              zIndex: 1000,
              backgroundColor: 'white',
              overflow: 'hidden'
            }}>
              <DigipinFinder />
            </div>
          } />
          <Route path="/pin2point-leaflet" element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <div style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: '100vh',
                zIndex: 1000,
                backgroundColor: 'white',
                overflow: 'hidden'
              }}>
                <Pin2PointLeaflet />
              </div>
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </div>
  );
};

function App() {
  return (
    <GoogleMapsProvider>
      <Router>
        <AddressProvider>
          <AppContent />
        </AddressProvider>
      </Router>
    </GoogleMapsProvider>
  );
}

export default App;
